import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import {
  millisecondsLeft,
  sessionEndedMessage,
  SESSION_END_REASONS,
  type Capability,
  type PasswordStanding,
  type Role,
  type SessionEndReason,
} from '@jt/domain';
import { api, SESSION_ENDED_EVENT, type SessionEndedDetail } from './api';
import { browserStorage, clearNavMenus } from './nav-menu';
import { DB_NAME, pending } from './offline-queue';
import {
  browserEnvironment,
  keepPreferences,
  restorePreferences,
  sessionChannel,
  SIGNED_OUT_REASON_KEY,
  wipeClientState,
} from './sign-out';

export type SessionUser = {
  id: string;
  orgId: string;
  email: string;
  name: string;
  role: Role;
  /** While true, the console is replaced by the change-password screen. */
  mustChangePassword?: boolean;
  /** ISO. When this session ends, whatever anybody is in the middle of. */
  sessionExpiresAt?: string;
  /** How old the password is, so the console can warn before it bites. */
  password?: PasswordStanding;
  /**
   * Whether to offer this account the practice drills.
   *
   * A preference an admin sets in Settings, not a permission — `quiz:play` is
   * held by every role and stays that way. It arrives on the session because
   * the alternative was a settings request from a technician, who cannot read
   * settings and should not have to in order to be shown a menu.
   *
   * Optional and defaulted to true by `drillsVisible` below, so an older API
   * that does not send it shows the drills rather than hiding them.
   */
  drillsVisible?: boolean;
};

type SessionValue = {
  user: SessionUser | null;
  capabilities: readonly Capability[];
  loading: boolean;
  /** UI convenience only — the API enforces this independently on every call. */
  can: (capability: Capability) => boolean;
  /**
   * Whether the drills are offered to whoever is signed in.
   *
   * True when there is no answer either way, which covers the moment before
   * the session has loaded and an API that predates the flag. Hiding a
   * practice quiz for a second costs nothing; hiding it because a field was
   * missing is the silent kind of bug.
   */
  drillsVisible: boolean;
  signIn: (
    email: string,
    password: string,
    extra?: { remember?: boolean },
  ) => Promise<void>;
  /** `reason` is shown on the sign-in screen once, then cleared. */
  signOut: (reason?: string) => Promise<void>;
  /**
   * Re-read who we are from the server.
   *
   * For the paths that establish a session without going through `signIn` —
   * activating an account from an emailed link, coming back from a provider.
   * Those set the cookies server-side, so the console is holding a stale
   * "nobody" until something asks again.
   */
  refresh: () => Promise<void>;
  /**
   * Milliseconds until this session ends, or null when there is no session.
   *
   * Exposed so a screen can say so. Read once per render rather than ticked,
   * because nothing needs a live countdown — the sign-out is scheduled, not
   * polled.
   */
  millisLeft: number | null;
};

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Whether a technician's offline actions are waiting to be sent.
 *
 * Asked before signing out, because the server is about to be told to clear
 * this browser's storage and that is where they wait. When the answer is
 * unknown it is "yes": keeping storage a little longer is a smaller harm than
 * destroying work that exists nowhere else.
 */
async function hasOfflineWork(): Promise<boolean> {
  try {
    if (typeof indexedDB === 'undefined') return false;
    if ('databases' in indexedDB) {
      const databases = await indexedDB.databases();
      if (!databases.some((database) => database.name === DB_NAME)) return false;
    }
    const waiting = await Promise.race([
      pending(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
    ]);
    return waiting === null || waiting.length > 0;
  } catch {
    return true;
  }
}

/** Set once this page has begun leaving, so the many ways out run once. */
let leaving = false;

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const channel = useMemo(() => sessionChannel(), []);

  const { data, isLoading } = useQuery({
    queryKey: ['session'],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });

  const expiresAt = data?.user?.sessionExpiresAt
    ? new Date(data.user.sessionExpiresAt)
    : null;

  const value = useMemo<SessionValue>(() => {
    const capabilities = data?.capabilities ?? [];

    return {
      user: data?.user ?? null,
      capabilities,
      loading: isLoading,
      can: (capability) => capabilities.includes(capability),
      // Defaulted to true, so the moment before the session loads and an API
      // that predates the flag both show the drills rather than hiding them.
      drillsVisible: data?.user?.drillsVisible ?? true,
      signIn: async (email, password, extra) => {
        await api.login(email, password, extra);
        // Any other tab still showing the previous person reloads as this one.
        channel.announce({ type: 'signed-in' });
        // A new session starts with every menu section shut, whoever sat here
        // before. (The menu also checks the session stamp, which covers the
        // ways in that do not pass through here.)
        clearNavMenus(browserStorage());

        /*
         * Started, not awaited.
         *
         * This used to await the whole invalidation, which waits for every
         * query mounted on the page to refetch before sign-in is considered
         * done. The sign-in screen holds its button disabled until then, so a
         * single slow or stalled refetch left somebody looking at "Signing
         * in..." with no error, no console, and no way to try again short of
         * reloading - while the server had already recorded the sign-in as
         * successful.
         *
         * Nothing here needs the refetch to have finished. The session query
         * re-renders the application away from this screen on its own, and
         * the rest arrive as they arrive.
         */
        void queryClient.invalidateQueries();
      },
      refresh: async () => {
        // Everything, not just the session: capabilities decide which queries
        // are even allowed, so a stale list elsewhere would render a console
        // for the wrong person.
        await queryClient.invalidateQueries();
      },
      /**
       * Signing out must always succeed locally, whatever the server says.
       *
       * Two things went wrong in the first version. The logout call was
       * awaited without a catch, so an expired session — which answers 401 —
       * threw before anything was cleared and the button silently did nothing.
       * And `queryClient.clear()` alone left the mounted session observer
       * holding its previous result, so even on the happy path the console
       * could stay on screen.
       *
       * A full reload sidesteps both. It also drops every piece of in-memory
       * state, which is what someone pressing "sign out" on a shared laptop
       * actually wants.
       */
      millisLeft: expiresAt ? millisecondsLeft(expiresAt) : null,
      /**
       * End this browser's session.
       *
       * `reason` is left for the sign-in screen to read once and clear, so
       * somebody who was working a moment ago is told what happened rather
       * than dropped on a bare form to draw their own conclusion.
       */
      signOut: (reason?: string) => leave(reason, { tellServer: true, tellOthers: true }),
    };
    /*
     * `expiresAt` is deliberately not a dependency. It is a `new Date` built
     * from `data` on every render, so listing it would rebuild this value every
     * time and hand a new context object to every screen that reads the
     * session. `data` is the real input and it is already here.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, queryClient, channel]);

  /**
   * Leave, completely (ADR-038). Every way out comes through here: the
   * button, the deadline, the server refusing the session, another tab.
   *
   *  1. Ask the server to revoke the session and clear this browser, unless
   *     another tab already did, which is what `tellServer: false` means.
   *  2. Forget everything held in memory and storage here.
   *  3. Tell the other tabs, so they do the same at once.
   *  4. Hide the page and replace this history entry with the sign-in
   *     screen, so Back does not lead to a copy of the console.
   */
  async function leave(reason: string | undefined, how: { tellServer: boolean; tellOthers: boolean }) {
    if (leaving) return;
    leaving = true;

    const keepOffline = await hasOfflineWork();
    const preferences = keepPreferences(browserStorage());
    if (how.tellServer) {
      try {
        await api.logout({ keepOffline });
      } catch {
        // Already forgotten, or unreachable. Everything below still happens.
      }
    }
    await wipeClientState(browserEnvironment(queryClient), { keepOffline });
    // Covered by the wipe; kept so the menu's own rule is visible here too.
    clearNavMenus(browserStorage());
    restorePreferences(browserStorage(), preferences);
    if (how.tellOthers) channel.announce({ type: 'signed-out' });

    try {
      if (reason) sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
    } catch {
      /* A browser refusing storage is not worth failing a sign-out over. */
    }

    // Nothing of the console stays on screen, even for the instant before
    // the browser moves, and nothing visible is left for the back/forward
    // cache to keep, should it keep this page at all.
    document.documentElement.style.visibility = 'hidden';
    window.location.replace('/login');
  }

  const signedIn = data?.user != null;
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;

  /*
   * The server refused this session: ended from another device, idle, or
   * ended to protect the account. The event is only raised for a 401 the guard
   * marked with `X-Session-Ended`, which it does only when a session cookie was
   * presented and refused — so a page opened after the session ended elsewhere
   * still says why, and an ordinary 401 never wipes anything.
   */
  useEffect(() => {
    const onEnded = (event: Event) => {
      const detail = (event as CustomEvent<SessionEndedDetail>).detail;
      if (!detail?.reason) return;
      const reason = (SESSION_END_REASONS as readonly string[]).includes(detail?.reason ?? '')
        ? (detail.reason as SessionEndReason)
        : null;
      void leave(reason ? sessionEndedMessage(reason) : detail?.message, {
        tellServer: true,
        tellOthers: true,
      });
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Another tab signed out, or somebody signed in there. */
  useEffect(() => {
    const stop = channel.listen((message) => {
      if (message.type === 'signed-out') {
        void leave('You signed out in another tab.', { tellServer: false, tellOthers: false });
      } else {
        // A new sign-in in another tab: this tab may be showing somebody else.
        queryClient.clear();
        window.location.reload();
      }
    });
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  /*
   * Coming back to the tab asks the server again. A session ended from another
   * device is noticed the moment somebody looks at this one, not at their next
   * click.
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && signedInRef.current) {
        void queryClient.invalidateQueries({ queryKey: ['session'] });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [queryClient]);

  useAutomaticSignOut(expiresAt, value.signOut);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Sign out at the deadline, rather than waiting to be refused.
 *
 * A session lasts twelve hours and then stops. Without this, the first anybody
 * knows of it is a page that will not load or a form that throws — and the
 * natural reading of that is "the application is broken", not "my day ended".
 *
 * So the console schedules its own exit on the deadline the server gave it, and
 * announces why on the way out. One timer per session, not a poll: the deadline
 * is a fixed instant and there is nothing to check for.
 *
 * The clock skew case is deliberate. If a device's clock is wrong the remaining
 * time is wrong too, which is why the server still refuses an expired session
 * independently — this is a courtesy, never the enforcement.
 */
function useAutomaticSignOut(
  expiresAt: Date | null,
  signOut: (reason?: string) => Promise<void>,
) {
  const fired = useRef(false);

  useEffect(() => {
    if (!expiresAt) return;
    fired.current = false;

    const go = () => {
      if (fired.current) return;
      fired.current = true;
      // Handed to the sign-out, which leaves it for the sign-in page to read
      // once after it has cleared everything else, so somebody returning to a
      // bare sign-in screen knows why they are looking at it.
      void signOut(
        'Your session reached its time limit, so you were signed out. Sign in again to carry on.',
      );
    };

    /*
     * Re-armed in chunks rather than set once.
     *
     * `setTimeout` keeps its delay in a signed 32-bit integer, so anything past
     * about 24.8 days overflows and fires **immediately**. The comment that
     * used to sit here said "this is at most twelve hours, so a single timer is
     * safe" — which was true until "keep me signed in" made a session thirty
     * days, and then it was exactly wrong.
     *
     * What that looked like: sign in, and be signed out again within the
     * second. Not a refusal — the password was right and the session was really
     * created — so nothing on the screen or in the server logs said anything
     * was wrong, and from the outside it was indistinguishable from being
     * unable to sign in at all.
     *
     * Waking every 24 days to look at the clock again costs nothing and cannot
     * overflow whatever the session length becomes next.
     */
    let timer: ReturnType<typeof setTimeout>;

    const arm = () => {
      const remaining = millisecondsLeft(expiresAt);
      if (remaining <= 0) {
        go();
        return;
      }
      timer = setTimeout(arm, nextDelay(remaining));
    };

    arm();
    return () => clearTimeout(timer);
  }, [expiresAt, signOut]);
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}

/**
 * The longest a `setTimeout` may safely be asked to wait.
 *
 * Its delay is a signed 32-bit integer of milliseconds. One millisecond more
 * and it wraps to a negative number, which fires on the next tick — so a timer
 * meant for next month runs now.
 */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * How long to sleep before looking at the clock again.
 *
 * Never more than a `setTimeout` can hold, and never less than nothing. The
 * caller re-arms until the deadline actually arrives, so a session of any
 * length is counted down correctly by repetition rather than by one long wait
 * that the platform cannot honour.
 */
export function nextDelay(remainingMs: number): number {
  return Math.max(0, Math.min(remainingMs, MAX_TIMEOUT_MS));
}
